/**
 * Ponte Behavior → Queue V2.
 * Envia o buffer de snapshots + eventos críticos como um único evento LOW na fila.
 */

import type { GomarkePixelConfig } from '../types';
import type { HeartbeatPayload } from './types';
import { enqueueEvent } from '../core/queue';
import {
  takeBehaviorBufferForQueue,
  registerPendingBehaviorFlush,
  clearBehaviorBuffer,
} from './buffer';

const MAX_BEHAVIOR_PAYLOAD_BYTES = 60_000;

/**
 * Se o buffer tiver dados, monta um evento BEHAVIOR_SUMMARY com o payload de lote,
 * enfileira com prioridade low e mantém cópia pendente até ack do backend.
 */
export function flushBehaviorBufferToQueue(
  config: GomarkePixelConfig,
  version: string,
  visitorId: string,
  sessionId: string,
  scriptUrl?: string
): void {
  const payload = takeBehaviorBufferForQueue(visitorId, sessionId);
  if (!payload) return;

  const eventData = {
    ...payload,
    hostname: typeof window !== 'undefined' ? window.location.hostname : '',
    page_url: typeof window !== 'undefined' ? window.location.href : '',
    path: typeof window !== 'undefined' ? window.location.pathname : '',
    timestamp: Date.now(),
  };

  const serialized = JSON.stringify(eventData);
  if (serialized.length > MAX_BEHAVIOR_PAYLOAD_BYTES) {
    if (config.debug) {
      console.warn(
        '[GomarkePixel] Behavior batch payload too large, skipping enqueue:',
        serialized.length,
        'bytes'
      );
    }
    clearBehaviorBuffer();
    return;
  }

  const queueItemId = enqueueEvent(
    {
      category: 'ENGAGEMENT',
      type: 'BEHAVIOR_SUMMARY',
      config,
      sessionId,
      visitorId,
      version,
      scriptUrl,
      eventData,
    },
    'low'
  );
  if (queueItemId) {
    registerPendingBehaviorFlush(queueItemId, payload);
  }
}

/**
 * Enfileira HEARTBEAT leve (presence) — sem snapshots nem agregações.
 * Reutiliza a mesma fila e prioridade low do behavior batch.
 */
export function enqueueHeartbeatEvent(
  config: GomarkePixelConfig,
  version: string,
  visitorId: string,
  sessionId: string,
  payload: HeartbeatPayload,
  scriptUrl?: string
): string {
  const eventData = {
    ...payload,
    hostname: typeof window !== 'undefined' ? window.location.hostname : '',
    page_url: typeof window !== 'undefined' ? window.location.href : '',
    path: typeof window !== 'undefined' ? window.location.pathname : '',
    timestamp: Date.now(),
  };

  if (config.debug || isHeartbeatDebug()) {
    const size = JSON.stringify(eventData).length;
    console.log('[Behavior] HEARTBEAT payload:', size, 'bytes');
  }

  return enqueueEvent(
    {
      category: 'ENGAGEMENT',
      type: 'HEARTBEAT',
      config,
      sessionId,
      visitorId,
      version,
      scriptUrl,
      eventData,
    },
    'low'
  );
}

function isHeartbeatDebug(): boolean {
  return (
    typeof window !== 'undefined' &&
    !!(window as unknown as { __behaviorDebug?: boolean }).__behaviorDebug
  );
}
